import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { sortBy } from 'lodash'
import config from '@/config'
import lang from '@/lang'
import customerConfig from '@/modules/customer/config'
import { makeStyles } from '@mui/styles'
import supplierConfig from '@/modules/supplier/config'
import { SelectInput } from '@/modules/base/components/inputs'
import request from '@/modules/base/util/request'
import { useAppSelector } from '@/redux/store'
import theme from '@/theme'
import OwnEmailRestriction from './own-email-restriction'

const selectStyles = {
  placeholder: (base) => ({ ...base, color: theme.textSecondary }),
  dropdownIndicator: (base) => ({
    ...base,
    color: theme.palette.text.primary,
    '&:hover': { ...base['&:hover'], color: theme.palette.text.primary },
  }),
}

const useStyles = makeStyles((theme) => ({
  customOptionWrapper: {
    borderBottom: `1px solid ${theme.borderColor}`,
    padding: 10,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: '#f5fafd',
    },
  },
  customOptionName: {
    fontWeight: '600',
  },
  customOptionEmail: {
    fontSize: '12px',
  },
}))

const fetchCustomer = (customerId, contactId) => {
  let recipient = null

  if (!customerId) {
    return {
      contacts: [],
      recipient,
    }
  }

  const onRequestSuccess = ({ body: { data } }) => {
    const contacts = data.contactPersons
      .filter((contactPerson) => !!contactPerson.email)
      .map(({ email, name, lastName, id }) => ({
        email, name, lastName, id,
      }))

    const sortedContacts = sortBy(contacts, 'lastName')

    if (data.email) {
      const { email, name, id } = data
      const contact = { email, name, id }

      sortedContacts.unshift(contact)
      recipient = contact
    }

    if (contactId) {
      recipient = contacts.find(({ id }) => id === contactId) || recipient
    }

    return {
      contacts: sortedContacts,
      recipient,
    }
  }

  return request(`${customerConfig.resourceUrl}/${customerId}`, { auth: true, isConcurrent: true })
    .then(onRequestSuccess)
    .catch(() => {
      return {
        contacts: [],
        recipient,
      }
    })
}

const fetchSupplier = (supplierId, contactId) => {
  let recipient = null

  if (!supplierId) {
    return {
      contacts: [],
      recipient,
    }
  }

  const onRequestSuccess = ({ body: { data } }) => {
    const contacts = data.contactPersons
      .filter((contactPerson) => !!contactPerson.email)
      .map(({ email, name, lastName, id }) => ({
        email, name, lastName, id,
      }))

    const sortedContacts = sortBy(contacts, 'lastName')

    if (data.email) {
      const { email, name, id } = data
      const contact = { email, name, id }

      sortedContacts.unshift(contact)
      recipient = contact
    }

    if (contactId) {
      recipient = contacts.find(({ id }) => id === contactId) || recipient
    }

    return {
      contacts: sortedContacts,
      recipient,
    }
  }

  return request(`${supplierConfig.resourceUrlSingle}/${supplierId}`, { auth: true, isConcurrent: true })
    .then(onRequestSuccess)
    .catch(() => {
      return {
        contacts: [],
        recipient,
      }
    })
}

const getComponents = (classes) => ({
  Option: ({
    data: { name, email, id },
    innerRef,
    innerProps,
  }) => {
    return (
      <div className={classes.customOptionWrapper} ref={innerRef} {...innerProps}>
        {id
          ? (
            <>
              <div className={classes.customOptionName}>{name}</div>
              <div className={classes.customOptionEmail}>{email}</div>
            </>
          )
          : (
            <div>
              <span className={classes.customOptionName}>{email}{' '}</span>
              {lang.recipientEmailInput.add}
            </div>
          )}
      </div>
    )
  },
})

export interface IRecipient {
  email: string
  name: string
}
interface ICustomerEmailInput {
  customerId?: string
  contactId?: string
  supplierId?: string
  recipients: IRecipient[]
  customClass?: string
  dataQsId: string
  required?: boolean
  label?: string
  errorMessage?: string
  autoSelectDefault?: boolean
  onChange: (recipients: IRecipient[]) => void
  // Suppress the per-field trial-restriction hint when the caller shows a single combined
  // notice for several of these inputs instead (e.g. recipients + CC + BCC together).
  hideOwnEmailRestriction?: boolean
  // While the tenant is restricted to its own email (trial), auto-fill this field with the
  // user's own address. Set to false for optional fields (CC/BCC) so they stay empty instead
  // of duplicating the value already forced into the required recipient field.
  autoFillOwnEmailWhenRestricted?: boolean
  // Keep the label styled as active even while the input itself is readOnly/disabled.
  keepLabelActive?: boolean
  // Force the field readOnly regardless of the allowExternalEmails-based restriction above.
  readOnly?: boolean
}

const RecipientEmailInput = (props: ICustomerEmailInput) => {
  const {
    customerId,
    supplierId,
    contactId,
    customClass = '',
    onChange,
    recipients: initialRecipients = [],
    dataQsId,
    required = false,
    label = lang.recipientEmailInput.email,
    errorMessage,
    autoSelectDefault = true,
    hideOwnEmailRestriction = false,
    autoFillOwnEmailWhenRestricted = true,
    keepLabelActive = true,
    readOnly = false,
  } = props

  const [contactOptions, setContactOptions] = useState(null)
  const [recipients, setRecipients] = useState(initialRecipients)
  const classes = useStyles()
  const allowExternalEmails = useAppSelector(state => state.tenant.settings?.allowExternalEmails)
  const userInfo = useAppSelector(state => state.user?.info)
  const userOption = useMemo(() => {
    if (!userInfo?.email) { return }
    const { email, firstName, lastName } = userInfo
    const name = [firstName, lastName].filter(Boolean).join(' ')
    return { email, name: name || email }
  }, [userInfo])

  const onOptionCreated = useCallback((option) => {
    if (!allowExternalEmails) { return }

    const recipient = { email: option, name: option }
    setRecipients([...(recipients || []), recipient])
    setContactOptions([...(contactOptions || []), recipient])
  }, [allowExternalEmails, recipients, contactOptions])

  const onSelectChange = (options, { action }) => {
    if (action === 'remove-value' && !options) {
      setRecipients([])
    } else {
      setRecipients(options)
    }
  }

  const selectOptions = useMemo(() => {
    if (!contactOptions) {
      return null
    }

    return {
      name: 'recipients',
      label: required ? `${label}*` : label,
      errorMessage,
      value: recipients,
      customComponents: getComponents(classes),
      allowCreate: allowExternalEmails,
      notAsync: true,
      options: contactOptions || [],
      readOnly: readOnly || (!allowExternalEmails && autoFillOwnEmailWhenRestricted),
      labelReadOnly: keepLabelActive ? false : undefined,
      styles: selectStyles,
      customSelectOptions: {
        placeholder: lang.recipientEmailInput.placeholder,
        valueKey: 'email',
        labelKey: 'name',
        isClearable: false,
        autoFocus: true,
        isMulti: true,
        toolTipKey: 'email',
        onCreateOption: onOptionCreated,
        onChange: onSelectChange,
        isValidNewOption: (option) => config.emailCheck.test(option),
      },
    }
  }, [allowExternalEmails, autoFillOwnEmailWhenRestricted, contactOptions, recipients, classes, onOptionCreated, required, label, errorMessage, keepLabelActive, readOnly])

  useEffect(() => {
    if (!allowExternalEmails) {
      // Own email is always offered as a pickable option while restricted - only the
      // required field gets it pre-selected, optional fields (CC/BCC) start empty.
      const ownOption = userOption ? [userOption] : []
      setContactOptions(ownOption)
      if (autoFillOwnEmailWhenRestricted) {
        setRecipients(ownOption)
      }
    }
  }, [allowExternalEmails, autoFillOwnEmailWhenRestricted, userOption])

  useEffect(() => {
    if (!allowExternalEmails) { return }
    if (customerId) {
      fetchCustomer(customerId, contactId).then(({ recipient, contacts }) => {
        setRecipients(autoSelectDefault && recipient ? [recipient] : [])
        setContactOptions(contacts)
      })
    }
  }, [allowExternalEmails, customerId, contactId])

  useEffect(() => {
    if (!allowExternalEmails) { return }
    if (supplierId) {
      fetchSupplier(supplierId, contactId).then(({ recipient, contacts }) => {
        setRecipients(autoSelectDefault && recipient ? [recipient] : [])
        setContactOptions(contacts)
      })
    }
  }, [allowExternalEmails, supplierId, contactId])

  useEffect(() => {
    if (onChange) {
      onChange(recipients)
    }
  }, [recipients])

  const ownEmailEl = useMemo(() => {
    if (allowExternalEmails || hideOwnEmailRestriction) { return null }
    return (
      <OwnEmailRestriction hideInput />
    )
  }, [allowExternalEmails, hideOwnEmailRestriction])

  return (
    <div className={customClass}>
      {!!selectOptions && <SelectInput dataQsId={dataQsId} {...selectOptions} />}
      {ownEmailEl}
    </div>
  )
}

export default RecipientEmailInput
